import {
  SelectOptionOld,
  useModalOldContext,
  WSButtons,
  WSCentered,
  WSContainer,
  WSFlexBox,
  WSFormOld,
  WSPanel,
  WSSelectOld,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeUpdate } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payeesApi } from "@api";
import { queriesPayees } from "@query";
import React, { useState } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { ProjectOnboardingLayout } from "../../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { selectQuickbooksUserOptions } from "../../../../modules/Integrations/selectors/selectQuickbooksUserOptions";
import { WSQueries } from "../../../../query/WSQuery";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import { isQuickbooksAuthenticated } from "../../../../query/integrations/selectors";
import { selectorPayeeDisplayName } from "../../../../shared/selectors/selectorPayeeDisplayName";
import { bulkActionFactory } from "../../../../modules/BulkActions";
import { getChangedData } from "../../../../utils/getChangedData";
import styles from "./Quickbooks.module.scss";
import {
  INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
  QUICKBOOKS_ENTITY,
  RefreshModal
} from "./RefreshButton";

export const QuickbooksSetupStep6: React.FC<RouteComponentProps<any>> = ({
  history
}) => {
  const { openModal } = useModalOldContext();

  const qIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const qIntegrationsQuickbooksVendors =
    useQueryIntegrationsQuickbooksVendors();
  const queryPayees = queriesPayees.listPayees.useQueryAll({});

  const [error, setError] = useState("");

  const updatePayeesAction = bulkActionFactory({
    onStart() {
      setError("");
    },
    async action(data: { payeeId: string; vendorId: string | null }) {
      // v3: the QBO vendor↔contractor mapping lives on the per-payee
      // `PayeeUpdate.quickbooksVendorId` (SDK 1.20.0), the payee twin of the
      // client-side `Payer.quickbooksCustomerId`. QA: confirm the deployed
      // backend persists this and that the QBO sync engine reads the v3-written
      // field (see V3_MIGRATION_TRACKER).
      const payeeUpdate: PayeeUpdate = {};
      // A cleared select un-maps the contractor (v1 parity — sends null);
      // PayeeUpdate types the field as `string`, so widen once at this boundary.
      (
        payeeUpdate as { quickbooksVendorId?: string | null }
      ).quickbooksVendorId = data.vendorId;
      await payeesApi.updatePayee({ payeeId: data.payeeId, payeeUpdate });
    },
    onFinish({ failedItems }) {
      if (failedItems.length) {
        setError("Something went wrong!");
      } else {
        // The direct SDK write above doesn't auto-invalidate; refresh the
        // payee list this step reads so the mappings are reflected.
        void WSQueryCache.invalidateQueries(
          queriesPayees.listPayees.queryKeyBase
        );
        history.push("/member/settings/integrations/quickbooks/setup/step/7");
      }
    }
  });

  return (
    <ProjectOnboardingLayout progress={(100 / 8) * 6}>
      <WSQueries
        queries={{
          qIntegrationsQuickbooks,
          qIntegrationsQuickbooksVendors,
          queryPayees
        }}
      >
        {({ qIntegrationsQuickbooks, qIntegrationsQuickbooksVendors }) => {
          const payees = queryPayees.flatData ?? [];
          const integrationState = qIntegrationsQuickbooks.data;
          const vendors = qIntegrationsQuickbooksVendors.data;

          const quickbooksVendorOptions: SelectOptionOld[] =
            selectQuickbooksUserOptions(vendors);

          if (!isQuickbooksAuthenticated(integrationState)) {
            return (
              <Redirect to="/member/settings/integrations/quickbooks/setup" />
            );
          }

          const defaultFormData = payees.reduce(
            (res, payee) => ({
              ...res,
              [payee.id!]: payee.quickbooksVendorId ?? undefined
            }),
            {} as any
          );

          return (
            <WSContainer verticalPadding>
              <WSCentered span={{ m: "8" }}>
                <WSText.ParagraphSm color="gray500" mt="M" pt="M">
                  6 of 8
                </WSText.ParagraphSm>
                <WSText.Heading4>Map Vendors</WSText.Heading4>

                <WSPanel mt="2XL" mb="5XL">
                  <WSFormOld
                    onSubmit={async formData => {
                      const changedData = getChangedData(
                        defaultFormData,
                        formData
                      );

                      await updatePayeesAction.run(
                        Object.keys(changedData).map(payeeId => ({
                          payeeId,
                          vendorId: formData[payeeId] ?? null
                        }))
                      );
                    }}
                    defaultValues={defaultFormData}
                  >
                    <WSText.Heading5 mt="XL">
                      Import and associate QuickBooks vendors with Wingspan
                      contractors
                    </WSText.Heading5>

                    {payees.length > 0 ? (
                      <>
                        <WSText mt="XL">
                          Please map your existing contractors to vendors in
                          QuickBooks. Any contractors you don't map will have a
                          new vendor created in QBO alongside any payables
                          activity
                        </WSText>

                        <WSTable
                          mt="XL"
                          tableData={payees.map(payee => ({
                            id: payee.id!,
                            data: payee
                          }))}
                          columns={[
                            {
                              config: {
                                gridTemplateSizeMax: "1fr"
                              },
                              renderFunction: ({ data }) => {
                                const name = selectorPayeeDisplayName(data);

                                return (
                                  <WSTableCell
                                    avatar={{
                                      type: "text",
                                      text: name
                                    }}
                                    text={name}
                                    secondaryText={data.email}
                                  />
                                );
                              }
                            },
                            {
                              config: {
                                gridTemplateSizeMax: "1fr",
                                className: styles.column
                              },
                              renderFunction: ({ data }) => (
                                <WSFlexBox.CenterY>
                                  <WSFormOld.Field
                                    name={data.id!}
                                    component={WSSelectOld}
                                    componentProps={{
                                      placeholder: "Not mapped",
                                      options: quickbooksVendorOptions,
                                      searchable: true,
                                      cleanable: true,
                                      placeholderActions: [
                                        {
                                          label: "Resync QBO Vendors",
                                          icon: "refresh-v",
                                          callback() {
                                            openModal(
                                              INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
                                              {
                                                entity:
                                                  QUICKBOOKS_ENTITY.VENDORS
                                              }
                                            );
                                          }
                                        }
                                      ]
                                    }}
                                    label=""
                                  />
                                </WSFlexBox.CenterY>
                              )
                            }
                          ]}
                        />
                        <WSErrorMessage
                          mt="2XL"
                          contextKey="IntegrationsQuickbooksMapping"
                          error={error}
                        />
                      </>
                    ) : (
                      <>
                        <WSText mt="XL">
                          It appears you have no existing contractors in
                          Wingspan. You can skip this step.
                        </WSText>
                      </>
                    )}
                    <WSFlexBox.Center mt="2XL">
                      <WSButtons>
                        <WSFormOld.SubmitButton name="next">
                          Next
                        </WSFormOld.SubmitButton>
                      </WSButtons>
                    </WSFlexBox.Center>
                  </WSFormOld>
                </WSPanel>
              </WSCentered>
            </WSContainer>
          );
        }}
      </WSQueries>
      <RefreshModal />
    </ProjectOnboardingLayout>
  );
};
